import { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { Observable } from 'rxjs';
import { CreateResultDto } from '../dto/create-result-dto';
import { GuiOptionsDto } from '../dto/gui-options-dto';
import { ResultInfoGenericDto } from '../dto/ResultInfoDto';
import { Erma2Service } from '../services/erma2.service';
import { FileDownloadService } from '../services/file-download.service';
import { PdsMessageService } from '../Services/pds-message.service';
import { FormTouchUtil } from '../Utils/form-touch-util';

/**
 * Společný základ pro komponenty formulářů
 */
export abstract class FormBase {

	/**
	 * Formulář (naplněn v odvozené komponentě)
	 */
	form: UntypedFormGroup = null;

	/**
	 * Informace, že došlo k odeslání formuláře
	 */
	formSubmitAttempt: boolean = false;

	/**
	 * Přepínač blokování GUI při načítání
	 */
	blocked: boolean = false;

	/**
	 * Přepínač viditelnosti dialogu s výsledkem uložení/vytvoření PDF
	 */
	dialogVisible: boolean = false;

	/**
	 * True, pokud se právě validuje formulář před odesláním
	**/
	validationBeforeSubmit: boolean = false;

	/**
	 * Blokováni formu během zpracování odesílání do DS
	 **/
	isdsBlocked: boolean;

	/**
	 * viditelnost tlačítka pro odeslání do DS
	 **/
	isdsBtnVisible: boolean = false;

	private _options: GuiOptionsDto;

	/**
	 * Klíč ke komponentě messages
	 */
	protected readonly MSG_KEY: string;

	constructor(
		private downloadService: FileDownloadService,
		private formTouch: FormTouchUtil,
		protected pdsMessageService: PdsMessageService,
		private erma2Service: Erma2Service
	) {
		this.erma2Service.getOptions().subscribe(optionsRes => {
			this._options = optionsRes;
			if (this._options.atsId != void 0 && !!this._options.atsId) {
				this.isdsBtnVisible = true;
			}
		});
	}


	/**
	 * V odvozené třídě implementuje uložení dat (každý formulář má vlastní service)
	 */
	abstract callService(): Observable<ResultInfoGenericDto<CreateResultDto>>;

	/**
	 * Obsluha tlačítka "Uložit a vytisknout"
	 * @param isIsds - true, pokud se jedná o submit před odesíláním do DS
	 */
	onSubmit(isIsds: boolean) {
		this.validationBeforeSubmit = true;

		let subscription = this.form.statusChanges.subscribe(status => {
			if (status == 'VALID') {
				subscription.unsubscribe();
				this.validationBeforeSubmit = false;
				this._submit(isIsds);
			}
			else if (status == 'INVALID') {
				subscription.unsubscribe();
				this.formSubmitAttempt = true;
				this.validationBeforeSubmit = false;
				this.isdsBlocked = false;
			}
		});

		this.formTouch.touchAllFormFields(this.form);
	}


	/**
	 *  Odeslání formuláře, pokud je validní.
	 * @param isIsds - true, pokud se jedná o submit před odesíláním do DS
	 */
	protected _submit(isIsds: boolean) {
		this.blocked = !isIsds;
		this.isdsBlocked = isIsds;
		this.callService().subscribe({
			next: res => {
				if (res != void 0) {
					if (res.success) {
						var zadostId = res.data.id;
						if (isIsds && this._options != void 0) {
							window.location.href = this._options.isdsLoginUrlWithoutToken.replace("{0}", zadostId?.toString());
						} else {
							this.downloadService.downloadZadostPdf(res.data.id, res.data.token)
								.subscribe(res => {
									if (res.success) {
										this.pdsMessageService.add({ severity: 'success', text: 'Váš prohlížeč by vám měl nabídnout PDF soubor s vyplněným formulářem ke stažení nebo zobrazení.', key: this.MSG_KEY });
									} else {
										this.pdsMessageService.add({ severity: 'error', text: res.messages[0], key: this.MSG_KEY });
									}
									this.dialogVisible = true;
									this.blocked = false;
								});
						}
					} else {
						this.pdsMessageService.add({ severity: 'error', text: `Uložení žádosti se nezdařilo. ${res.messages[0]}`, key: this.MSG_KEY });
						this.dialogVisible = true;
						this.blocked = false;
					}
				}
				else {
					this.pdsMessageService.add({ severity: 'error', text: 'Server nevrátil odpověď. Zkuste to, prosím, znovu.', key: this.MSG_KEY });
					this.dialogVisible = true;
					this.blocked = false;
				}
			}, error: () => {
				this.pdsMessageService.add({ severity: 'error', text: 'Nastala neočekávaná chyba. Zkuste to, prosím, znovu.', key: this.MSG_KEY });
				this.dialogVisible = true;
				this.blocked = false;
			}
		});
	}

	/**
	 * Obsluha tlačítka zavření dialogu
	 */
	onClose() {
		this.dialogVisible = false;
	}

	/**
	 * Obsluha tlačítka vyčištění formuláře
	 */
	onClear() {
		this.dialogVisible = false;
		this.formSubmitAttempt = false;
		this.form.reset();
		this.form.patchValue({
			Rok: new Date().getFullYear(),
			Datum: new Date().toISOString().substring(0, 10)
		});
	}


	/**
	 * Vyvolá revalidaci formulářové položky a dle potřeby označí jako touched.
	 * @param control
	 * @param markAsTouch
	 */
	protected revalidateControl(control: AbstractControl, markAsTouch: boolean): void {
		control?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
		if (markAsTouch) {
			if (control instanceof UntypedFormGroup || control instanceof UntypedFormArray) {
				control?.markAllAsTouched()
			}
			else {
				control?.markAsTouched();
			}
		}
	}

	/**
	 * Při pokusu o tisk nevalidního formuláře vypíše chybovou hlášku.
	 * @param formKey
	 */
	messageServiceFormError(formKey: string) {
		if (!this.form.valid) {
			this.pdsMessageService.add({ key: formKey, severity: 'error', text: 'Formulář obsahuje chyby a žádost proto nelze vytisknout ani odeslat. Opravte, prosím, zvýrazněné hodnoty.' });
		}
	}
}
